import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';

import {
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_matched_item_list as ProductItem,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_matched_item_list_item_attribute_list as ItemAttributeList,
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_shipping_fee as ShippingFee,
  PdpCatalogFulfillmentType,
  PdpCatalogSalesStatus,
  PdpShippingFeeType,
} from '../graphql/select-option_types.2';
import {
  CustomMatchingProductInfo,
  CustomOptionsInfo,
  CustomOptionValueList,
  SelectedItem,
  SelectedOption,
} from '../types/option-type';

export const genItemKey = (item: SelectedItem) => {
  const optionsValueArray = flatten(item.options).map((o) => o.selected_value);
  const textOptionsValueArray = flatten(item.text_options).map((o) => o.selected_value);
  const optionsStringData = optionsValueArray.concat(textOptionsValueArray).join(';');

  return `${item.id};${optionsStringData}`;
};

export function isEventProduct(product_id: string) {
  const product_id_list = [
    '750249',
    '750250',
    '750251',
    '750252',
    '750253',
    '750254',
    '750255',
    '750256',
    '167684',
    '167685',
    '167686',
    '167687',
    '167688',
    '167689',
    '167690',
    '167691',
  ];
  return product_id_list.includes(product_id);
}

const mergeOptionName = (selectedValue: string, value: string, notLastDepth: boolean) => {
  return (selectedValue ? selectedValue + '/' + value : value) + (notLastDepth ? '/' : '');
};

const search = (str: string, comparisonStr: string, searchMethod: 'startsWith' | 'strict') => {
  if (searchMethod === 'startsWith') {
    return str.startsWith(comparisonStr);
  } else if (searchMethod === 'strict') {
    return str === comparisonStr;
  }
};

export const findOptionInAllOption = (
  list: ProductItem[],
  selectedValue: string,
  value: string,
  curOptionIndex: number,
  requiredOnSale?: boolean,
) => {
  return list.find((o) => {
    const valueStr = o.item_attribute_list.map((ov) => ov.value).join('/');
    const lastDepth = o.item_attribute_list.length === curOptionIndex + 1;
    const optionName = mergeOptionName(selectedValue, value, !lastDepth);
    const isSingleOption = o.item_attribute_list.length === 1;

    if (isSingleOption) {
      return valueStr === optionName;
    }

    return (
      search(valueStr, optionName, lastDepth ? 'strict' : 'startsWith') &&
      (requiredOnSale ? o.sales_status === PdpCatalogSalesStatus.ON_SALE : true)
    );
  });
};

export const initData = (optionsData: CustomOptionsInfo, only_zigzig_option: boolean): CustomOptionsInfo => {
  const { matched_item_list, ...restData } = optionsData;
  const isZigzinProduct = isZigZinFulfillment(optionsData.fulfillment_type);
  const clone_options_data = Object.assign(cloneDeep(restData), { matched_item_list });
  const matchingOption = clone_options_data.matching_catalog_product_info;
  const lastMatchingOptionIndex = (clone_options_data.matching_catalog_product_info?.option_list.length ?? 0) - 1;

  if (!matchingOption) {
    return optionsData;
  }

  processMatchingOptionValues(matchingOption, !isZigzinProduct, lastMatchingOptionIndex);

  refinedNameOfOptionItemList(clone_options_data);

  handleProductOptions(clone_options_data, isZigzinProduct, only_zigzig_option);

  return clone_options_data;
};

const refinedNameOfOptionItemList = (options: CustomOptionsInfo) => {
  options.matched_item_list.forEach((o) => {
    // MEMO: item_attribute_list.value랑 순서가 다르게 정의된 item_attribute_list.name이 있어 재정의
    o.name = o.item_attribute_list.map((attr) => attr.value).join('/');
  });
};

export const setIsZigzinOfValueList = (optionsData: CustomOptionsInfo, shipping_fee: ShippingFee | null) => {
  const { matched_item_list, ...restData } = optionsData;
  const clone_options_data: CustomOptionsInfo = Object.assign(cloneDeep(restData), { matched_item_list });
  const isZigzinProduct = isZigZinFulfillment(optionsData.fulfillment_type);
  const last_option_index = clone_options_data.product_option_list.length - 1;

  clone_options_data.product_option_list.forEach((option, productOptionIndex) => {
    option.value_list.forEach((value) => {
      value.is_zigzin = isZigzinProduct;

      value.is_free_shipping = shipping_fee?.fee_type === PdpShippingFeeType.FREE;
      value.shipping_fee = shipping_fee?.base_fee ?? 0;

      if (productOptionIndex === last_option_index) {
        value.is_last_option = true;
      }
    });
  });

  return clone_options_data;
};

export const getItemOptions = (itemOptionList: ItemAttributeList[], length: number) => {
  return itemOptionList
    .slice(0, length)
    .map((o) => o.value_id)
    .join(';');
};

export const updateOptionValueList = (
  options: SelectedOption[][],
  covertOptionData: CustomOptionsInfo,
): SelectedOption[][] => {
  try {
    options.forEach((option_item) => {
      option_item.forEach((o, idx) => {
        const item = covertOptionData.product_option_list[idx];
        if (item) {
          o.value_list = item.value_list;
        }
      });
    });
    return options;
  } catch (e) {
    return options;
  }
};

const processMatchingOptionValues = (
  matchingOption: CustomMatchingProductInfo,
  isZigzinProduct: boolean,
  lastMatchingOptionIndex: number,
) => {
  matchingOption?.option_list.forEach((option, index) =>
    option.value_list.forEach((value) => {
      value.is_zigzin = isZigzinProduct;

      if (index === lastMatchingOptionIndex) {
        value.is_last_option = true;
        value.is_free_shipping = matchingOption.shipping_fee?.fee_type === PdpShippingFeeType.FREE;
        value.shipping_fee = matchingOption.shipping_fee?.base_fee ?? 0;
      }
    }),
  );
};

const handleProductOptions = (optionsData: CustomOptionsInfo, isZigzinProduct: boolean, onlyZigzigOption: boolean) => {
  optionsData.product_option_list.forEach((option, productOptionIndex) => {
    const isFirstIndex = productOptionIndex === 0;
    const filterMatchingOptionValueList: CustomOptionValueList[] = [];

    const matchingOptionValueList =
      optionsData.matching_catalog_product_info?.option_list[productOptionIndex]?.value_list ?? [];

    if (isZigzinProduct && !onlyZigzigOption) {
      // MEMO: 첫번째 옵션만 value 일치 검사
      if (isFirstIndex) {
        filterMatchingOptionValueList.push(
          ...matchingOptionValueList.filter((o) => option.value_list.find((ov) => ov.value === o.value)),
        );
      } else {
        filterMatchingOptionValueList.push(...matchingOptionValueList);
      }
      option.value_list = option.value_list.concat(filterMatchingOptionValueList);
    }

    if (!isZigzinProduct) {
      const sortedOptionList: CustomOptionValueList[] = [];

      option.value_list.forEach((o) => {
        sortedOptionList.push(o);

        const findIndex = matchingOptionValueList.findIndex((o2) => o2.value === o.value);

        if (findIndex > -1) {
          sortedOptionList.push(matchingOptionValueList[findIndex]);
          matchingOptionValueList.splice(findIndex, 1);
        }
      });
      option.value_list = matchingOptionValueList.concat(sortedOptionList);
    }
  });
};

function isZigZinFulfillment(fulfillment_type: PdpCatalogFulfillmentType) {
  return (
    fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN || fulfillment_type === PdpCatalogFulfillmentType.VENDOR_FLEX
  );
}
